import { useState, useCallback } from 'react';
import { Modal, message } from 'antd';
import { d204Api, FD140 } from '../../../services/d204Api';

interface UseD204ApprovalOptions {
  record: FD140 | null;
  isEditing: boolean;
  loadRecordWithNames: (id: string) => Promise<void>;
}

export function useD204Approval({ record, isEditing, loadRecordWithNames }: UseD204ApprovalOptions) {
  const [approvalHistoryVisible, setApprovalHistoryVisible] = useState(false);

  const handleSubmit = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要將入庫單 ${record.fd140_01} 送審？`,
      okText: '送審',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d204Api.submitForApproval(record.fd140_01);
          message.success(resp.message || '送審成功');
          await loadRecordWithNames(record.fd140_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '送審失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleApprove = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准入庫單 ${record.fd140_01}？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d204Api.approve(record.fd140_01);
          message.success(resp.message || '核准成功');
          await loadRecordWithNames(record.fd140_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleReject = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回入庫單 ${record.fd140_01}？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d204Api.reject(record.fd140_01);
          message.success(resp.message || '退回成功');
          await loadRecordWithNames(record.fd140_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '退回失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleWriteoff = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${record.fd140_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d204Api.writeoff(record.fd140_01);
          message.success(resp.message || '沖銷成功');
          await loadRecordWithNames(record.fd140_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '沖銷失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleViewHistory = useCallback(() => {
    if (!record) return;
    setApprovalHistoryVisible(true);
  }, [record]);

  return {
    approvalHistoryVisible,
    setApprovalHistoryVisible,
    handleSubmit,
    handleApprove,
    handleReject,
    handleWriteoff,
    handleViewHistory,
  };
}
